import { useState, useEffect, useCallback } from 'react';
import type { PlanInfo, UpgradeRequest, UpgradeResponse } from '../types';
import { fetchPlanInfo, upgradePlan } from '../services/referral-api';

interface UsePlanReturn {
  planInfo: PlanInfo | null;
  isLoading: boolean;
  error: string | null;
  upgrade: (data: UpgradeRequest) => Promise<UpgradeResponse>;
  refetch: () => Promise<void>;
}

export function usePlan(): UsePlanReturn {
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchPlanInfo();
      setPlanInfo(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plan info');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const upgrade = useCallback(async (data: UpgradeRequest): Promise<UpgradeResponse> => {
    const result = await upgradePlan(data);
    await refetch();
    return result;
  }, [refetch]);

  return { planInfo, isLoading, error, upgrade, refetch };
}
